import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import "../mainContainer/MainContainer.css";
import { getUserRole, getUserId } from "../../utils/tokenUtils";
import "./ManageShopifyApp.css";
import "./AppsList.css";
import Sidebar from "../sidebar/Sidebar";
import SidebarBrand from "../sidebarBrand/SidebarBrand";
import TopBar from "../../components/TopBar/TopBar";
import ConfirmModal from "../../components/ConfirmModal/ConfirmModal";
import FieldMappingModal from "../catalog/FieldMappingModal";
import ManageShopifyModal from "./ManageShopifyModal";
import {
  ThirdPartyApp,
  ThirdPartyAppsService,
} from "../../services/thirdPartyApps/thirdPartyAppsService";
import { ShopifyService } from "../../services/shopify/shopifyService";
import { HelenService } from "../../services/helen/helenService";
import { useToast } from "../../components/common/ToastManager/ToastContext";
import { AppLogo } from "./icons";
const APP_OPTIONS = [
  {
    id: "shopify",
    name: "Shopify",
    subtitle: "Connect your Shopify store",
    enabled: true,
  },
  {
    id: "optimziely",
    name: "Optimizely",
    subtitle: "Connect Optimizely app",
    enabled: true,
  },
  {
    id: "salesforce",
    name: "Salesforce",
    subtitle: "Coming soon...",
    enabled: false,
  },
  {
    id: "megento",
    name: "Megento",
    subtitle: "Coming soon...",
    enabled: false,
  },
];
const AppsList: React.FC = () => {
  const navigate = useNavigate();
  const role = getUserRole();
  const SidebarComponent = role === "Admin" ? SidebarBrand : Sidebar;
  const [isConnecting, setIsConnecting] = useState(false);
  const [connectingAppId, setConnectingAppId] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [apps, setApps] = useState<ThirdPartyApp[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { showToast } = useToast();
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [appToDelete, setAppToDelete] = useState<ThirdPartyApp | null>(null);
  const [showFieldMappingModal, setShowFieldMappingModal] = useState(false);
  const [manageShopifyAppId, setManageShopifyAppId] = useState<number | null>(null);
  const [openMenuAppId, setOpenMenuAppId] = useState<number | null>(null);
  const [showAppSelectModal, setShowAppSelectModal] = useState(false);
  const [showShopifyModal, setShowShopifyModal] = useState(false);
  const [shopifyErrors, setShopifyErrors] = useState<{
    shop?: string;
    installLink?: string;
    clientSecret?: string;
    bot?: string;
  }>({});
  const [bots, setBots] = useState<any[]>([]);
  const [selectedShopifyBot, setSelectedShopifyBot] = useState("");
  const [shopifyBotError, setShopifyBotError] = useState<string | null>(null);
  const [shopifyInstallLink, setShopifyInstallLink] = useState("");
  const [shopifyClientSecret, setShopifyClientSecret] = useState("");
  const handleAddApp = () => {
    setShowAppSelectModal(true);
  };
  const handleEditApp = (id: number) => {
    navigate(`/apps/add?appId=${id}`);
  };
  const toggleMenu = (id: number) => {
    setOpenMenuAppId((prev) => (prev === id ? null : id));
  };
  const handleDeleteClick = (app: ThirdPartyApp) => {
    setAppToDelete(app);
    setShowDeleteModal(true);
    setOpenMenuAppId(null);
  };
  const handleConfirmDelete = async () => {
    if (!appToDelete) return;
    setIsDeleting(true);
    try {
      await ThirdPartyAppsService.deleteApp(appToDelete.id);
      const wasOptimizely = appToDelete.name.toLowerCase().includes("optimizely");
      setApps((prev) => prev.filter((a) => a.id !== appToDelete.id));
      if (wasOptimizely) {
        window.dispatchEvent(new CustomEvent("optimizely-app-changed", { detail: { connected: false } }));
      }
      showToast("App disconnected successfully", "success");
    } catch (err) {
      console.error("Failed to disconnected app", err);
      showToast("Failed to disconnected app. Please try again.", "error");
    } finally {
      setIsDeleting(false);
      setShowDeleteModal(false);
      setAppToDelete(null);
    }
  };
  const handleCancelDelete = () => {
    setShowDeleteModal(false);
    setAppToDelete(null);
  };
  const handleOpenShopifyModal = () => {
    setShopifyInstallLink("");
    setSelectedShopifyBot("");
    setShopifyClientSecret("");
    setShopifyErrors({});
    setSelectedShopifyBot("");
    setShopifyBotError(null);
    setShowAppSelectModal(false);
    setShowShopifyModal(true);
  };
  const handleCloseShopifyModal = () => {
    setShowShopifyModal(false);
  };
  const handleConnectOptimizely = async () => {
    try {
      setIsConnecting(true);
      setConnectingAppId("optimziely");
      const userId = Number(getUserId());
      await ThirdPartyAppsService.createApp({
        name: "Optimizely",
        user_id: userId,
        helen_id: 0,
      });
      setShowAppSelectModal(false);
      showToast("Optimizely connected successfully!", "success");
      const optimizelyPath = role === "Admin" ? "/admin/optimizely" : "/user/optimizely";
      navigate(optimizelyPath);
    } catch (err: any) {
      showToast(
        err?.response?.data?.message || "Failed to connect Optimizely Catalog",
        "error"
      );
      setIsConnecting(false);
      setConnectingAppId(null);
    }
  };
  const handleConnectShopify = async () => {
    const errors: any = {};

    if (!shopifyInstallLink.trim()) {
      errors.installLink = "Install link is required";
    }

    if (!shopifyClientSecret.trim()) {
      errors.clientSecret = "Client  secret is required";
    }

    if (!selectedShopifyBot) {
      errors.bot = "Please select a Helen";
    }

    if (Object.keys(errors).length > 0) {
      setShopifyErrors(errors);
      return;
    }

    try {
      setIsConnecting(true);
      setShopifyErrors({});

      const response = await ShopifyService.installApp(
        shopifyInstallLink.trim(),
        shopifyClientSecret.trim(),
        selectedShopifyBot,
      );

      if (!response.data?.success) {
        setShopifyErrors({
          installLink: response.data?.message || "Failed to install app",
        });
        return;
      }

      setShowShopifyModal(false);
      window.open(response.data?.url, "_blank");
    } catch (err: any) {
      setShopifyErrors({
        installLink:
          err?.response?.data?.message || "Failed to generate install link",
      });
    } finally {
      setIsConnecting(false);
    }
  };
  const fetchApps = async () => {
    try {
      setIsLoading(true);
      const response = await ThirdPartyAppsService.getApps();
      setApps(response.data?.data || []);
    } catch (err: any) {
      console.error("Failed to load apps", err);
      setError(
        err?.response?.data?.error ||
          "Failed to load apps. Please try again.",
      );
    } finally {
      setIsLoading(false);
    }
  };
  useEffect(() => {
    fetchApps();
  }, []);
  useEffect(() => {
    const fetchBots = async () => {
      try {
        const response = await HelenService.getBotsAssignedToUser("all");
        if (response.status === 200 && response.data?.userAssignedBots) {
          setBots(response.data.userAssignedBots);
        }
      } catch (err) {
        console.error("Failed to load Helens for Shopify", err);
      }
    };
    fetchBots();
  }, []);
  return (
    <div className="dashboard-container">
      <TopBar />
      <div className="third-party-apps-wrapper">
        <div className="third-party-apps-header-row">
          <h2 className="third-party-apps-title">Connected Apps</h2>
          <div className="third-party-apps-actions">
            <button
              type="button"
              className="submit-button add-apps-button"
              onClick={handleAddApp}
            >
              + Add App
            </button>
          </div>
        </div>
        <div className="third-party-apps-card">
          {isLoading ? (
            <div className="no-apps-text">Loading apps...</div>
          ) : error ? (
            <div className="no-apps-text">{error}</div>
          ) : apps.length === 0 ? (
            <div className="no-apps-text">No apps connected yet.</div>
          ) : (
            <div className="apps-card-list">
              {apps.map((app) => {
                const isOptimizely = app.name.toLowerCase().includes("optimizely");
                return (
                <div className="integration-card-new" key={app.id}>
                  {/* LOGO */}
                  <div className="logo-wrapper">
                    <AppLogo appKey={app.name} />
                  </div>
                  {/* TITLE */}
                  <div className="integration-title-new">{app.name}</div>
                  {/* STATUS */}
                  <div className="status-pill-new">● Connected</div>
                  {/* ACTIONS */}
                  <div className="integration-actions-new">
                    <button
                      className="manage-btn-new"
                      disabled={isDeleting}
                      onClick={() => {
                        if (isOptimizely) {
                          setShowFieldMappingModal(true);
                        } else {
                          setManageShopifyAppId(app.id);
                        }
                      }}
                    >
                      Manage
                    </button>
                    <button
                      className="disconnect-btn-new"
                      disabled={isDeleting}
                      onClick={() => handleDeleteClick(app)}
                    >
                      Disconnect
                    </button>
                  </div>
                </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
      {showDeleteModal && appToDelete && (
        <ConfirmModal
          title=""
          message={`Are you sure you want to disconnect "${appToDelete.name}"?`}
          confirmText="Disconnect"
          cancelText="Cancel"
          onConfirm={handleConfirmDelete}
          onCancel={handleCancelDelete}
          isLoading={isDeleting}
        />
      )}
      {showAppSelectModal && (
        <div
          className="api-docs-overlay"
          onClick={() => setShowAppSelectModal(false)}
        >
          <div className="api-docs-modal" onClick={(e) => e.stopPropagation()}>
            <div className="api-docs-header">
              <h3>Select App to Add</h3>
              <button
                className="api-docs-close-btn"
                onClick={() => setShowAppSelectModal(false)}
              >
                ×
              </button>
            </div>
            <div className="apps-card-body">
              <div className="apps-modal-card-list">
                {APP_OPTIONS.map((option) => (
                  <div className={`app-card ${!option.enabled ? "disabled" : ""}`} key={option.id}>
                    <div className="app-card-header">
                      <div className="app-name-cell">
                        <div className="app-icon-circle">
                          <AppLogo
                            appKey={option.id}
                            className={`app-icon-image ${
                              !option.enabled ? "disabled-logo" : ""
                            }`}
                          />
                        </div>
                        <div>
                          <div className="app-name-text">{option.name}</div>
                          <div className="app-card-meta">{option.subtitle}</div>
                        </div>
                      </div>
                    </div>
                    <div className="app-card-body">
                      {(() => {
                        const isOptimizelyAlreadyConnected =
                          option.id === "optimziely" &&
                          apps.some((a) => a.name.toLowerCase().includes("optimizely"));
                        const isShopifyAlreadyConnected =
                          option.id === "shopify" &&
                          apps.some((a) => a.name.toLowerCase().includes("shopify"));
                        const alreadyConnected = isOptimizelyAlreadyConnected || isShopifyAlreadyConnected;
                        return (
                          <button
                            className="submit-button"
                            disabled={
                              !option.enabled ||
                              alreadyConnected ||
                              isConnecting ||
                              (option.id === "optimziely" && role !== "Admin")
                            }
                            onClick={() => {
                              if (option.id === "shopify") {
                                handleOpenShopifyModal();
                              } else if (option.id === "optimziely") {
                                handleConnectOptimizely();
                              }
                            }}
                          >
                            {connectingAppId === option.id
                              ? "Connecting..."
                              : alreadyConnected
                              ? "Connected"
                              : option.id === "optimziely" && role !== "Admin"
                              ? "Admin Only"
                              : option.enabled
                              ? "Connect"
                              : "Coming Soon"}
                          </button>
                        );
                      })()}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
      {showShopifyModal && (
        <div className="api-docs-overlay">
          <div className="api-docs-modal" onClick={(e) => e.stopPropagation()}>
            <div className="api-docs-header">
              <h3>Connect Shopify Store</h3>
              <button
                className="api-docs-close-btn"
                onClick={handleCloseShopifyModal}
              >
                ×
              </button>
            </div>
            <div className="api-docs-body">
              <div className="form-group">
                <label>
                  Install Link <span className="required"> *</span>
                </label>
                <input
                  type="text"
                  placeholder="Enter your Shopify install link"
                  value={shopifyInstallLink}
                  onChange={(e) => {
                    setShopifyInstallLink(e.target.value);
                    setShopifyErrors((prev) => ({ ...prev, installLink: "" }));
                  }}
                />
                {shopifyErrors.installLink && (
                  <span className="error-text">
                    {shopifyErrors.installLink}
                  </span>
                )}
              </div>
              <div className="form-group">
                <label>
                  Client Secret<span className="required"> *</span>
                </label>
                <input
                  type="password"
                  placeholder="Enter your Shopify client secret"
                  value={shopifyClientSecret}
                  onChange={(e) => {
                    setShopifyClientSecret(e.target.value);
                    setShopifyErrors((prev) => ({ ...prev, clientSecret: "" }));
                  }}
                />
                {shopifyErrors.clientSecret && (
                  <span className="error-text">
                    {shopifyErrors.clientSecret}
                  </span>
                )}
              </div>
              <div className="form-group">
                <label>
                  Assign Helen<span className="required"> *</span>
                </label>
                <select
                  value={selectedShopifyBot}
                  onChange={(e) => {
                    setSelectedShopifyBot(e.target.value);
                    setShopifyErrors((prev) => ({ ...prev, bot: "" }));
                  }}
                >
                  <option value="">Select Helen</option>
                  {bots.map((bot) => (
                    <option key={bot.id} value={bot.id}>
                      {bot?.helen_name
                        ? bot.helen_name.charAt(0).toUpperCase() +
                          bot.helen_name.slice(1).replace(/_/g, " ")
                        : "Unknown"}
                    </option>
                  ))}
                </select>
                {shopifyErrors.bot && (
                  <span className="error-text">{shopifyErrors.bot}</span>
                )}
              </div>
            </div>
            <div className="api-docs-footer">
              <button
                className="cancel-button"
                onClick={handleCloseShopifyModal}
              >
                Cancel
              </button>
              <button
                className="submit-button"
                onClick={handleConnectShopify}
                disabled={isConnecting}
              >
                {isConnecting ? (
                  <span className="btn-loader">
                    <span className="spinner"></span> Connecting...
                  </span>
                ) : (
                  "Connect"
                )}
              </button>
            </div>
          </div>
        </div>
      )}
      <FieldMappingModal
        isOpen={showFieldMappingModal}
        onClose={() => setShowFieldMappingModal(false)}
      />
      <ManageShopifyModal
        isOpen={manageShopifyAppId !== null}
        onClose={() => setManageShopifyAppId(null)}
        appId={manageShopifyAppId ?? 0}
      />
    </div>
  );
};
export default AppsList;
